import classNames from 'classnames';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import type { RBACUser, RoleMember } from '../../../types';
import { useGetUsersQuery } from '../../graph/services/graphApi';
import UserAutocomplete from '../../users/components/UserAutocomplete';
import {
  selectMemberAdds,
  selectMemberDeletes,
  selectOwnerAdds,
  selectOwnerDeletes,
  stageAdd,
  stageDelete,
} from '../services/roleManagementSlice';

import StagedCountBadges from './StagedCountBadges';
import UserDisplayItem from './UserDisplayItem';

// Members and Owners are the same panel over two Graph relationships: a list of
// user rows with staged add/remove and an "add" autocomplete over the tenant. Both
// buckets stage user-shaped items, so this one component drives both — see the
// MembersPanel / OwnersPanel wrappers that supply the bucket and labels.
const selectorsFor = {
  members: { adds: selectMemberAdds, deletes: selectMemberDeletes },
  owners: { adds: selectOwnerAdds, deletes: selectOwnerDeletes },
} as const;

type Props = {
  bucket: 'members' | 'owners';
  title: string;
  placeholder: string;
  emptyLabel: string;
  // The committed list (members or owners); undefined while loading.
  items: (RBACUser | RoleMember)[] | undefined;
  // When true the panel fills its flex parent and the list scrolls within that
  // height (used for Members, which drives the page-tall right column). Otherwise
  // the list keeps a fixed cap and the panel sizes to its content (used for Owners).
  fill?: boolean;
};

export default function UserListPanel({
  bucket,
  title,
  placeholder,
  emptyLabel,
  items,
  fill = false,
}: Props) {
  const dispatch = useAppDispatch();
  const { data: users } = useGetUsersQuery();

  const adds = useAppSelector(selectorsFor[bucket].adds);
  const deletes = useAppSelector(selectorsFor[bucket].deletes);

  // Exclude current entries and already-staged adds so the autocomplete only offers
  // people who can actually be added — picking an existing one would otherwise no-op.
  const excludedIds = new Set([...(items ?? []), ...adds].map((item) => item.id));
  const addCandidates = (users ?? []).filter(
    (user) => user.displayName && user.id && user.accountEnabled && !excludedIds.has(user.id)
  );

  const addedIds = new Set(adds.map((item) => item.id));
  const deletedIds = new Set(deletes.map((item) => item.id));
  const rows = [...adds, ...(items ?? [])];

  return (
    <div
      className={classNames('bg-slate-50 dark:bg-gray-800 rounded-lg p-4', {
        'flex flex-col flex-1 min-h-0': fill,
      })}
    >
      <div className="flex flex-row items-center justify-between gap-2 pb-2">
        <div className="flex flex-row items-center gap-2 min-w-0">
          <div className="font-bold truncate">{title}</div>
          <div className="shrink-0">
            <StagedCountBadges addCount={adds.length} deleteCount={deletes.length} />
          </div>
        </div>
        <div className="w-72 shrink-0">
          <UserAutocomplete
            items={addCandidates}
            onSelect={(user) => dispatch(stageAdd({ bucket, item: user }))}
            clearOnSelect
            placeholder={placeholder}
          />
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm opacity-60">{emptyLabel}</div>
      ) : (
        <div
          className={classNames('border border-base-200 rounded-lg overflow-hidden', {
            'flex-1 min-h-0': fill,
          })}
        >
          <ul
            className={classNames('divide-y divide-base-200 overflow-y-auto', {
              'h-full': fill,
              'max-h-[36rem]': !fill,
            })}
          >
            {rows.map((row) => {
              const added = addedIds.has(row.id);
              const deleted = deletedIds.has(row.id);
              return (
                <UserDisplayItem
                  key={added || deleted ? row.id + 'pending' : row.id}
                  user={row}
                  added={added}
                  deleted={deleted}
                  onDelete={() => dispatch(stageDelete({ bucket, item: row }))}
                />
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
